import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Text as RNText,
  Pressable,
  TextInput,
} from 'react-native';
import {
  Bevel,
  Button,
  Divider,
  RunningMan,
  Text,
  Window,
  colors,
  fontSizes,
  fonts,
  makeStyles,
  useTheme,
} from '@ogaim/ui';
import { sameScreenName, normalizeScreenName, type Message } from '@ogaim/core';
import { useStore } from '../store';
import { play } from '../sounds';
import { RichText } from '../components/RichText';
import {
  RichTextEditor,
  emptyRichText,
  toBody,
  type RichTextValue,
} from '../components/RichTextEditor';

export interface ImWindowProps {
  screenName: string;
  onClose: () => void;
  active?: boolean;
  /** False when the host draws the window frame (the web desktop). */
  frame?: boolean;
}

/** A 1:1 IM conversation: transcript on top, formatting bar and composer below. */
export function ImWindow({ screenName, onClose, active = true, frame = true }: ImWindowProps) {
  const theme = useTheme();
  const styles = useStyles();
  const key = normalizeScreenName(screenName);
  const convo = useStore((s) => s.conversations[key]);
  const me = useStore((s) => s.me);
  const send = useStore((s) => s.send);
  const notifyTyping = useStore((s) => s.notifyTyping);
  const noteActivity = useStore((s) => s.noteActivity);
  const buddy = useStore((s) =>
    s.buddies.find((b) => sameScreenName(b.screenName, screenName))
  );

  const [draft, setDraft] = useState<RichTextValue>(emptyRichText);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  const messages = convo?.messages ?? [];
  const lastId = messages[messages.length - 1]?.id;

  // Pin to the newest message whenever the transcript grows.
  useEffect(() => {
    if (lastId) requestAnimationFrame(() => scrollRef.current?.scrollToEnd({ animated: true }));
  }, [lastId]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last && me && !sameScreenName(last.from, me.screenName)) play('imReceive');
  }, [lastId]);

  const onChangeDraft = useCallback(
    (next: RichTextValue) => {
      setDraft(next);
      setSendError(null);
      noteActivity();
      const busy = next.text.length > 0 || next.images.length > 0;
      notifyTyping(screenName, busy ? 'TYPING' : 'NONE');
    },
    [screenName, notifyTyping, noteActivity]
  );

  const outgoing = toBody(draft);
  const canSend = outgoing.length > 0 && !sending;

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    const previous = draft;
    // Clear optimistically so the composer feels instant; restore on failure.
    setDraft(emptyRichText);
    try {
      await send(screenName, outgoing);
      play('imSend');
    } catch (err: any) {
      setDraft(previous);
      setSendError(err?.message ?? 'Message could not be sent.');
      play('error');
    } finally {
      setSending(false);
    }
  };

  const typingLabel =
    convo?.theirTyping === 'TYPING'
      ? `${screenName} is typing…`
      : convo?.theirTyping === 'TYPED'
        ? `${screenName} has entered text.`
        : buddy?.presence === 'AWAY' && buddy.awayMessage
          ? `${screenName} is away.`
          : '';

  const statusBar = (
    <Text disabled style={styles.statusText} numberOfLines={1}>
      {sendError ?? typingLabel}
    </Text>
  );

  const body = (
    <>
      {buddy?.presence === 'AWAY' && buddy.awayMessage ? (
        <Bevel variant="sunken" background="#FFFFCC" style={styles.awayBanner} contentStyle={styles.awayInner}>
          <View>
            <Text style={styles.awayName}>{screenName} is away:</Text>
            <RichText body={buddy.awayMessage} baseStyle={styles.awayText} />
          </View>
        </Bevel>
      ) : null}

      <Bevel variant="sunken" background={theme.colors.fieldBg} style={styles.transcript}>
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={styles.transcriptContent}
          onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: false })}
        >
          {convo?.loading && messages.length === 0 ? (
            <Text disabled>Loading conversation…</Text>
          ) : messages.length === 0 ? (
            <Text disabled>No messages yet. Say something.</Text>
          ) : (
            messages.map((m) => {
              const mine = !!me && sameScreenName(m.from, me.screenName);
              return (
                <MessageLine
                  key={m.id}
                  message={m}
                  mine={mine}
                  // `from` on a message is the normalized identifier. The
                  // capitalisation the sender chose lives on their profile, so
                  // prefer that wherever it is known.
                  senderName={
                    mine
                      ? me?.screenName ?? m.from
                      : buddy?.screenName ?? convo?.screenName ?? m.from
                  }
                />
              );
            })
          )}
        </ScrollView>
      </Bevel>

      <RichTextEditor
        value={draft}
        onChange={onChangeDraft}
        onSubmit={submit}
        editable={!sending}
        submitOnEnter
        placeholder="Type a message"
        accessibilityLabel="Message"
      />

      <View style={styles.sendRow}>
        <Button title="Send" onPress={submit} disabled={!canSend} primary style={styles.sendButton} />
      </View>
    </>
  );

  if (!frame) {
    return (
      <View style={styles.frameless}>
        <View style={styles.framelessBody}>{body}</View>
        <Bevel variant="sunken" style={styles.framelessStatus} contentStyle={styles.framelessStatusInner}>
          {statusBar}
        </Bevel>
      </View>
    );
  }

  return (
    <Window
      title={`Instant Message from ${screenName}`}
      active={active}
      onClose={onClose}
      icon={<RunningMan size={14} />}
      statusBar={statusBar}
    >
      {body}
    </Window>
  );
}

function MessageLine({
  message,
  mine,
  senderName,
}: {
  message: Message;
  mine: boolean;
  senderName: string;
}) {
  const styles = useStyles();
  const time = useMemo(() => formatTime(message.sentAt), [message.sentAt]);

  return (
    <View style={styles.messageLine}>
      <RichText
        body={message.body}
        prefix={
          <RNText style={[styles.sender, mine ? styles.senderMine : styles.senderTheirs]}>
            {senderName}:{' '}
          </RNText>
        }
        suffix={<RNText style={styles.timestamp}> {time}</RNText>}
      />
    </View>
  );
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  let h = d.getHours();
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `(${h}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')} ${suffix})`;
}

const useStyles = makeStyles((t) => ({
  awayBanner: { marginBottom: 3 },
  awayInner: { padding: 5 },
  awayText: { fontSize: t.fontSizes.ui, lineHeight: 15 },
  awayName: { fontWeight: 'bold' },

  transcript: { flex: 1 },
  transcriptContent: { padding: 4, gap: 2 },
  messageLine: {},
  // AIM coloured the two sides of a conversation red and blue.
  sender: { fontWeight: 'bold' },
  senderMine: { color: '#FF0000' },
  senderTheirs: { color: '#0000FF' },
  timestamp: { fontSize: 10, color: t.colors.disabledText },
  bold: { fontWeight: 'bold' },
  italic: { fontStyle: 'italic' },
  underline: { textDecorationLine: 'underline' },

  sendRow: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 3 },
  sendButton: { minWidth: 80 },
  statusText: { fontSize: 10 },

  // Frameless: the host draws the window; this is just the interior.
  frameless: { flex: 1, backgroundColor: t.colors.face, padding: 3 },
  framelessBody: { flex: 1, minHeight: 0 },
  framelessStatus: { marginTop: 3, height: 20 },
  framelessStatusInner: { justifyContent: 'center', paddingHorizontal: 4 },
}));
